/* eslint-disable */
import React, { useCallback, useMemo, useState } from "react";
import { CoreAppProvider } from "./CoreAppProvider";
import { useMedia } from "./MediaContext";
import { useFrame } from "./FrameContext";
import { useModel } from "./ModelContext";
import { useComputedValues } from "./ComputedValuesContext";
import { Tasks } from "@rapid/types/flows";
import type { Annotation } from "@rapid/types/annotation";
import { ComputedValuesProvider } from "./ComputedValuesProvider";
import { FrameProvider } from "./FrameProvider";
import { MediaProvider } from "./MediaProvider";
import { ModelProvider } from "./ModelProvider";
import { FlowContextType, FlowContext } from "./FlowContext";
import { useCoreApp } from "./CoreAppContext";
import { Prediction } from "@rapid/utils/api/inference";
import { useNavigate } from "react-router-dom";
import { resetAutoInvokeStatus } from "@rapid/utils/autoInvokeStatus";
import {
    useCurrentMedia,
    useFlattenedVideoSourceId,
    useMarkFrameAsReviewed,
    useSetUserAnnotationsSnapshotForFrame
} from "@rapid/store";
import { useMediaUtils } from "@rapid/hooks/useMediaUtils";
import { AutoPredictions } from "@rapid/components/AutoPredictions";
import { AutoPredictionsProvider } from "./AutoPredictionsProvider";

// Glues individual contexts into single object.
const AggregatedFlowContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const coreApp = useCoreApp();
    const media = useMedia();
    const frame = useFrame();
    const model = useModel();
    const computed = useComputedValues();
    const [flowLocked, setFlowLocked] = useState(false);
    const [isExtractingWithAI, setIsExtractingWithAI] = useState(false);
    const navigate = useNavigate();
    const currentMedia = useCurrentMedia();
    const flattenedVideoSourceId = useFlattenedVideoSourceId();
    const markFrameAsReviewed = useMarkFrameAsReviewed();
    const setUserAnnotationsSnapshotForFrame = useSetUserAnnotationsSnapshotForFrame();
    const { setCurrentMedia } = useMediaUtils();
    const effectiveAnnotationFileId = flattenedVideoSourceId || currentMedia?.file.id;

    // Optimized frame annotation setters
    const setCurrentFrameAnnotations = useCallback(
        (annotationsOrUpdater: Annotation[] | ((prev: Annotation[]) => Annotation[])) => {
            const frameId = currentMedia?.frame?.id;
            const canonicalFrameSegment =
                currentMedia?.frame?.metadata?.segment ??
                currentMedia?.file.metadata?.segment ??
                currentMedia?.frameSegment ??
                0;
            const shouldMirrorFlattenedVideoFrame =
                Boolean(flattenedVideoSourceId) &&
                Boolean(currentMedia?.file.id) &&
                !currentMedia?.file.isVideo &&
                currentMedia?.file.id !== flattenedVideoSourceId;
            const mirrorFileId = shouldMirrorFlattenedVideoFrame
                ? currentMedia?.file.id
                : undefined;

            const normalizeAnnotationsForTarget = (
                annotations: Annotation[],
                fileId: string,
                frameSegment: number
            ) =>
                annotations.map((annotation) => ({
                    ...annotation,
                    fileId,
                    frameId: frameId!,
                    frameSegment
                }));

            if (effectiveAnnotationFileId && frameId) {
                markFrameAsReviewed({
                    fileId: effectiveAnnotationFileId,
                    frameId,
                    frameSegment: canonicalFrameSegment
                });
            }

            let updatedFrameAnnotations: Annotation[] | null = null;

            model.setAnnotationsForFileAndFrameId((prev) => {
                if (!effectiveAnnotationFileId) return prev;
                if (!frameId) return prev;

                const prevFileAnnotations = prev[effectiveAnnotationFileId] || {};
                const prevFrameAnnotations = prevFileAnnotations[frameId] || [];

                const newFrameAnnotations =
                    typeof annotationsOrUpdater === "function"
                        ? annotationsOrUpdater(prevFrameAnnotations)
                        : annotationsOrUpdater;
                const canonicalFrameAnnotations = normalizeAnnotationsForTarget(
                    newFrameAnnotations,
                    effectiveAnnotationFileId,
                    canonicalFrameSegment
                );
                updatedFrameAnnotations = canonicalFrameAnnotations;

                const nextAnnotations = {
                    ...prev,
                    [effectiveAnnotationFileId]: {
                        ...prevFileAnnotations,
                        [frameId]: canonicalFrameAnnotations
                    }
                };

                if (!mirrorFileId) {
                    return nextAnnotations;
                }

                return {
                    ...nextAnnotations,
                    [mirrorFileId]: {
                        ...(nextAnnotations[mirrorFileId] || {}),
                        [frameId]: normalizeAnnotationsForTarget(
                            newFrameAnnotations,
                            mirrorFileId,
                            0
                        )
                    }
                };
            });

            if (effectiveAnnotationFileId && frameId && updatedFrameAnnotations) {
                setUserAnnotationsSnapshotForFrame(
                    effectiveAnnotationFileId,
                    frameId,
                    updatedFrameAnnotations
                );
            }
        },
        [
            model.setAnnotationsForFileAndFrameId,
            effectiveAnnotationFileId,
            flattenedVideoSourceId,
            currentMedia?.file.id,
            currentMedia?.file.isVideo,
            currentMedia?.file.metadata?.segment,
            currentMedia?.frame?.id,
            currentMedia?.frame?.metadata?.segment,
            currentMedia?.frameSegment,
            markFrameAsReviewed,
            setUserAnnotationsSnapshotForFrame
        ]
    );

    const setCurrentFramePredictions = useCallback(
        (predictionsOrUpdater: Prediction[] | ((prev: Prediction[]) => Prediction[])) => {
            model.setPredictionsForFileAndFrameSegment((prev) => {
                if (!currentMedia?.file.id) return prev;
                if (currentMedia?.frameSegment === undefined) return prev;

                const prevFilePredictions = prev[currentMedia?.file.id] || {};
                const prevFramePredictions = prevFilePredictions[currentMedia?.frameSegment] || [];

                const newFramePredictions =
                    typeof predictionsOrUpdater === "function"
                        ? predictionsOrUpdater(prevFramePredictions)
                        : predictionsOrUpdater;

                return {
                    ...prev,
                    [currentMedia?.file.id]: {
                        ...prevFilePredictions,
                        [currentMedia?.frameSegment]: newFramePredictions
                    }
                };
            });
        },
        [
            currentMedia?.file.id,
            currentMedia?.frameSegment,
            model.setPredictionsForFileAndFrameSegment
        ]
    );

    /**
     * Deletes an annotation from the current frame
     * @param annotationId - The ID of the annotation to delete
     */
    const deleteAnnotationFromCurrentFrame = useCallback(
        (annotationId: string) => {
            // Delete the annotation from the current frame
            setCurrentFrameAnnotations((prevFrameAnnotations: any) => {
                if (!prevFrameAnnotations) return [];
                return prevFrameAnnotations.filter((anno: any) => anno.id !== annotationId);
            });
        },
        [setCurrentFrameAnnotations]
    );

    /**
     * Deletes a prediction from the current frame
     * @param predictionId - The ID of the prediction to delete
     */
    const deletePredictionFromCurrentFrame = useCallback(
        (predictionId: string) => {
            // Delete the prediction from the current frame
            setCurrentFramePredictions((prevFramePredictions: any) => {
                if (!prevFramePredictions) return [];
                return prevFramePredictions.filter(
                    (pred: any) => pred.detection_id !== predictionId
                );
            });
        },
        [setCurrentFramePredictions]
    );

    // reset logic copied from original implementation
    const reset = useCallback(() => {
        Promise.resolve().then(() => {
            navigate("/");
            coreApp.setTask(Tasks.OBJECT_DETECTION);
            coreApp.setPreviouslySelectedObject(null);
            coreApp.setShowLabels(true);
            coreApp.setHideSidebar(false);
            media.setMediaItemIds([]);
            setCurrentMedia(undefined);
            frame.setSelectedAnnotationId(null);
            frame.setOrderedTimelineFramePreviewUrls({});
            model.setAnnotationsForFileAndFrameId({});
            model.setPredictionsForFileAndFrameSegment({});
            model.setIsDownloading(false);
            model.setTextPromptStr("");
            model.setPromptLabels(new Map());
            model.clearReviewedFrames();
            setFlowLocked(false);
            setIsExtractingWithAI(false);
            resetAutoInvokeStatus();
        });
    }, [coreApp, media, frame, model, navigate, setCurrentMedia]);

    // Flow legacy context value by merging sub contexts
    const value = useMemo<FlowContextType>(
        () => ({
            ...coreApp,
            ...media,
            ...frame,
            ...model,
            ...computed,
            flowLocked,
            setFlowLocked,
            isExtractingWithAI,
            setIsExtractingWithAI,
            reset,
            setCurrentFrameAnnotations,
            setCurrentFramePredictions,
            deleteAnnotationFromCurrentFrame,
            deletePredictionFromCurrentFrame
        }),
        [
            coreApp,
            media,
            frame,
            model,
            computed,
            reset,
            setCurrentFrameAnnotations,
            flowLocked,
            setFlowLocked,
            isExtractingWithAI,
            setIsExtractingWithAI,
            setCurrentFramePredictions,
            deleteAnnotationFromCurrentFrame,
            deletePredictionFromCurrentFrame
        ]
    );

    return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>;
};

export const FlowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <AutoPredictionsProvider>
        <MediaProvider>
            <FrameProvider>
                <ModelProvider>
                    <ComputedValuesProvider>
                        <CoreAppProvider>
                            <AggregatedFlowContextProvider>
                                <AutoPredictions />
                                {children}
                            </AggregatedFlowContextProvider>
                        </CoreAppProvider>
                    </ComputedValuesProvider>
                </ModelProvider>
            </FrameProvider>
        </MediaProvider>
    </AutoPredictionsProvider>
);
